import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { TaskFilter, NewTaskFilter } from "../../types";

export function useUpdateFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<NewTaskFilter>;
    }) => {
      const { error } = await supabase
        .from("task_filters")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },

    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.filters });

      const prevFilters = queryClient.getQueryData<TaskFilter[]>(queryKeys.filters);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useFilters out of its pending state and drop the app's loading
      // gate, and onError's snapshot guard could not put it back.
      queryClient.setQueryData<TaskFilter[]>(queryKeys.filters, (old) =>
        old ? old.map((f) => (f.id === id ? { ...f, ...updates } : f)) : old
      );

      return { prevFilters };
    },

    onError: (err, _, context) => {
      if (context?.prevFilters)
        queryClient.setQueryData<TaskFilter[]>(queryKeys.filters, context.prevFilters);
      Sentry.captureException(err, { extra: { mutation: "useUpdateFilter" } });
      toast.error("Failed to update filter. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}
